import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useScreenConfig } from "../hooks/useScreenConfig";
import { ComponentRenderer } from "./ComponentRenderer";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { useDarkMode } from "../contexts/DarkModeContext";
import { selectForms, selectUser } from "../state/selectors";
import {
    updateScreenFormField,
    setScreenFormData,
    setScreenLoading,
} from "../state/slices/formsSlice";
import { showNotification } from "../state/slices/notificationsSlice";
import { apiService } from "../services/api";
import { buildFormDataFromRecord } from "../state/utils/configUtils";
import { parseHrmsEmployeeProfileGetResponse } from "../utils/parseHrmsEmployeeProfileResponse";
import loaderLight from "../../public/images/loader_light.gif";
import loaderDark from "../../public/images/loader_dark.gif";
import { usePermissions } from "../hooks/usePermissions";
import type { ComponentConfig } from "../services/types/ComponentTypes";
import { getUserRole } from "../utils/visibilityUtils";
import { patchAttendanceSummaryScreenConfig } from "../utils/attendanceScreenConfig";
import {
    normalizeBillAllocationTab,
    patchBillAllocationHubScreenConfig,
    resolveScreenConfigForHub,
    type BillAllocationTab,
} from "../utils/billAllocationScreenConfig";
import {
    normalizeBillReleaseTab,
    patchBillReleaseHubScreenConfig,
    type BillReleaseTab,
} from "../utils/billReleaseScreenConfig";
import { patchJobcardSummaryScreenConfig } from "../utils/jobcardScreenConfig";
import { EditIcon, ScreenConfigJsonEditor } from "./ScreenConfigJsonEditor";

const EMPLOYEE_PROFILE_SCREEN_ID = "2217";

const isDevIndicatorEnabled = (): boolean => {
    if (import.meta.env.DEV) return true;
    try {
        return localStorage.getItem("dev_mode") === "true";
    } catch {
        return false;
    }
};

interface ScreenRendererProps {
    screenId: string;
    onActionTrigger: (actionId: string, data?: any) => void;
    refreshKey?: number;
    renderMode?: "page" | "popup";
}

export const ScreenRenderer: React.FC<ScreenRendererProps> = ({
    screenId,
    onActionTrigger,
    refreshKey,
    renderMode = "page",
}) => {
    const { config, loading: configLoading, error, source } = useScreenConfig(screenId, refreshKey);
    const dispatch = useAppDispatch();
    const formsState = useAppSelector(selectForms);
    const user = useAppSelector(selectUser);
    const { isDarkMode } = useDarkMode();
    const { hasPermission } = usePermissions();
    const profileLoadGenRef = useRef(0);
    const [configEditorOpen, setConfigEditorOpen] = useState(false);
    const [hubChildConfigs, setHubChildConfigs] = useState<{
        customer: ComponentConfig[] | null;
        vendor: ComponentConfig[] | null;
        refSettlement: ComponentConfig[] | null;
    }>({ customer: null, vendor: null, refSettlement: null });
    const isAccountingHubScreen = screenId === "196" || screenId === "197";
    const [hubChildLoading, setHubChildLoading] = useState(isAccountingHubScreen);

    // Get form data and loading state from formsState
    // Note: 'loadingByScreen' in formsState tracks DATA loading (e.g. initial values), 
    // while 'configLoading' tracks CONFIGURATION loading.
    // 'expressionCalculatingByScreen' keeps an overlay while ExpressionV2 settles after hydrate.
    const isDataLoading = formsState.loadingByScreen[screenId] || false;
    const isExpressionCalculating =
        formsState.expressionCalculatingByScreen[screenId] || false;
    const formData = formsState.formDataByScreen[screenId] || {};

    useEffect(() => {
        if (screenId !== "2200") return;
        const roleId = getUserRole();
        if (roleId === 2) return;
        if (formData.attendance_admin_tab) return;
        dispatch(
            updateScreenFormField({
                screenId,
                name: "attendance_admin_tab",
                value: "my",
            })
        );
    }, [screenId, formData.attendance_admin_tab, dispatch]);

    useEffect(() => {
        if (screenId !== "196" && screenId !== "197") return;
        let cancelled = false;

        const loadChildConfigs = async () => {
            setHubChildLoading(true);
            try {
                const [customerId, vendorId, refSettlementId] =
                    screenId === "197" ? ["189", "194", null] : ["187", "192", "186"];
                const [customer, vendor, refSettlement] = await Promise.all([
                    resolveScreenConfigForHub(customerId),
                    resolveScreenConfigForHub(vendorId),
                    refSettlementId
                        ? resolveScreenConfigForHub(refSettlementId)
                        : Promise.resolve([] as ComponentConfig[]),
                ]);
                if (!cancelled) {
                    setHubChildConfigs({
                        customer,
                        vendor,
                        refSettlement: refSettlement?.length ? refSettlement : null,
                    });
                }
            } finally {
                if (!cancelled) {
                    setHubChildLoading(false);
                }
            }
        };

        void loadChildConfigs();
        return () => {
            cancelled = true;
        };
    }, [screenId]);

    useEffect(() => {
        if (screenId !== "196") return;
        if (formData.bill_allocation_tab) return;

        const defaultTab: BillAllocationTab = hasPermission("187", "View")
            ? "customer"
            : hasPermission("192", "View")
              ? "vendor"
              : hasPermission("186", "View")
                ? "customer_ref_settlement"
                : "customer";

        dispatch(
            updateScreenFormField({
                screenId,
                name: "bill_allocation_tab",
                value: defaultTab,
            })
        );
    }, [
        screenId,
        formData.bill_allocation_tab,
        dispatch,
        hasPermission,
    ]);

    useEffect(() => {
        if (screenId !== "197") return;
        if (formData.bill_release_tab) return;

        const defaultTab: BillReleaseTab = hasPermission("189", "View")
            ? "customer"
            : hasPermission("194", "View")
              ? "vendor"
              : "customer";

        dispatch(
            updateScreenFormField({
                screenId,
                name: "bill_release_tab",
                value: defaultTab,
            })
        );
    }, [screenId, formData.bill_release_tab, dispatch, hasPermission]);

    // Combine loading states — data/config block mount; expression settle uses overlay after mount
    const isLoading =
        configLoading || isDataLoading || (isAccountingHubScreen && hubChildLoading);

    /**
     * Recursively removes components from a config array that the user lacks
     * permission for. A component is removed when it has a `permissionKey` and
     * that permission evaluates to false for the given `pScreenId`.
     *
     * NOTE: must be defined before any early returns to satisfy Rules of Hooks.
     */
    const filterByPermission = useMemo(() => {
        return function filter(components: ComponentConfig[], pScreenId: string | undefined): ComponentConfig[] {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c = comp as any;

                // Check permission gate on the component itself
                if (c.permissionKey && pScreenId) {
                    if (!hasPermission(pScreenId, c.permissionKey)) {
                        return acc; // drop this component
                    }
                }

                // Determine child screenId: if this component declares its own screenId, use it;
                // otherwise inherit the parent's
                const childScreenId: string | undefined = c.screenId ?? pScreenId;

                // Filter SummeryActions (row-action buttons on SummaryList)
                let filteredSummeryActions = c.SummeryActions;
                if (Array.isArray(c.SummeryActions) && childScreenId) {
                    filteredSummeryActions = c.SummeryActions.filter(
                        (action: any) =>
                            !action.permissionKey ||
                            hasPermission(childScreenId, action.permissionKey)
                    );
                }

                // Filter fabAction
                let filteredFabAction = c.fabAction;
                if (c.fabAction && c.fabAction.permissionKey && childScreenId) {
                    if (!hasPermission(childScreenId, c.fabAction.permissionKey)) {
                        filteredFabAction = undefined;
                    }
                }

                // Recurse into childComponents
                if (c.childComponents && Array.isArray(c.childComponents)) {
                    const filtered = filter(c.childComponents, childScreenId);
                    const newC = { ...c, childComponents: filtered };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;
                    acc.push(newC);
                } else {
                    const newC = { ...c };
                    if (filteredSummeryActions !== c.SummeryActions) newC.SummeryActions = filteredSummeryActions;
                    if (filteredFabAction === undefined) delete newC.fabAction;
                    else if (filteredFabAction !== c.fabAction) newC.fabAction = filteredFabAction;

                    if (filteredSummeryActions !== c.SummeryActions || filteredFabAction !== c.fabAction) {
                        acc.push(newC);
                    } else {
                        acc.push(comp);
                    }
                }

                return acc;
            }, []);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasPermission]);

    // Resolve the screenId declared in config (root ScreenGroupComponent may have screenId)
    // Use config ?? [] so this memo runs safely even before config is loaded.
    const configScreenId: string | undefined = (config?.[0] as any)?.screenId;

    // Apply permission-based filtering — MUST be before any early returns (Rules of Hooks)
    const filteredConfig = useMemo(
        () => filterByPermission(config ?? [], configScreenId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [config, configScreenId, filterByPermission]
    );

    // Screen 104: ensure manpower allocation summary action exists (API + local configs).
    // Screen 2200: MY/TEAM admin attendance (tabs, check-in, team columns, month filter).
    const screenSafeConfig = useMemo(() => {
        let cfg = filteredConfig ?? [];
        if (screenId === "104") {
            cfg = patchJobcardSummaryScreenConfig(cfg);
        }
        if (screenId === "2200") {
            const roleId = getUserRole();
            const adminTab = String(formData?.attendance_admin_tab || "my").toLowerCase();
            const adminMyTab = roleId !== 2 && adminTab !== "team";

            return patchAttendanceSummaryScreenConfig(cfg, {
                adminMyTab,
                roleId,
            });
        }

        if (screenId === "196") {
            let activeTab = normalizeBillAllocationTab(formData?.bill_allocation_tab);
            const tabVisible = (tab: BillAllocationTab): boolean => {
                if (tab === "customer") return hasPermission("187", "View");
                if (tab === "customer_ref_settlement") return hasPermission("186", "View");
                if (tab === "vendor") return hasPermission("192", "View");
                if (tab === "vendor_ref_settlement") return hasPermission("186", "View");
                return false;
            };
            if (!tabVisible(activeTab)) {
                if (hasPermission("187", "View")) activeTab = "customer";
                else if (hasPermission("186", "View")) activeTab = "customer_ref_settlement";
                else if (hasPermission("192", "View")) activeTab = "vendor";
                else if (hasPermission("186", "View")) activeTab = "vendor_ref_settlement";
            }

            return patchBillAllocationHubScreenConfig(cfg, {
                activeTab,
                customerConfig: hubChildConfigs.customer,
                vendorConfig: hubChildConfigs.vendor,
                refSettlementConfig: hubChildConfigs.refSettlement,
            });
        }

        if (screenId === "197") {
            let activeTab = normalizeBillReleaseTab(formData?.bill_release_tab);
            if (!hasPermission("189", "View") && hasPermission("194", "View")) {
                activeTab = "vendor";
            } else if (hasPermission("189", "View") && !hasPermission("194", "View")) {
                activeTab = "customer";
            }

            return patchBillReleaseHubScreenConfig(cfg, {
                activeTab,
                customerConfig: hubChildConfigs.customer,
                vendorConfig: hubChildConfigs.vendor,
            });
        }

        return cfg;
    }, [
        filteredConfig,
        screenId,
        formData?.attendance_admin_tab,
        formData?.bill_allocation_tab,
        formData?.bill_release_tab,
        hubChildConfigs.customer,
        hubChildConfigs.vendor,
        hubChildConfigs.refSettlement,
        hasPermission,
    ]);

    // In popup mode, strip "Back" buttons so the modal "X" is the only close affordance.
    // Also strip popup-only controls that aren't useful in constrained modal contexts.
    const popupSafeConfig = useMemo(() => {
        if (renderMode !== "popup") return screenSafeConfig;

        const stripPopupOnlyControls = (components: ComponentConfig[]): ComponentConfig[] => {
            return components.reduce<ComponentConfig[]>((acc, comp) => {
                const c: any = comp as any;
                const compType = c.componentType;
                const name = String(c.name ?? c.Label ?? "").trim().toLowerCase();
                const onPress = String(c.onPressAction ?? "").trim().toLowerCase();

                const shouldStrip =
                    String(compType).toLowerCase() === "button" &&
                    name === "back" &&
                    (onPress.startsWith("navigate_") || onPress === "navigate_back" || onPress === "cancel");

                // Keep all date filters in popup; we default them to today via formData.
                if (shouldStrip) return acc;

                if (Array.isArray(c.childComponents)) {
                    const nextChildren = stripPopupOnlyControls(c.childComponents);
                    if (nextChildren !== c.childComponents) {
                        acc.push({ ...(c as any), childComponents: nextChildren } as any);
                    } else {
                        acc.push(comp);
                    }
                    return acc;
                }

                acc.push(comp);
                return acc;
            }, []);
        };

        return stripPopupOnlyControls(screenSafeConfig ?? []);
    }, [screenSafeConfig, renderMode, screenId]);

    useLayoutEffect(() => {
        if (screenId !== EMPLOYEE_PROFILE_SCREEN_ID) return;
        if (configLoading || !config?.length) return;

        const employeeIdRaw = user?.employee_id;
        const employeeId =
            employeeIdRaw !== undefined && employeeIdRaw !== null
                ? String(employeeIdRaw).trim()
                : "";

        const gen = ++profileLoadGenRef.current;
        const ac = new AbortController();

        dispatch(
            setScreenLoading({
                screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                isLoading: true,
            })
        );

        (async () => {
            try {
                const endpoint = employeeId
                    ? `/pabws/hrms/employee/profile/${encodeURIComponent(employeeId)}`
                    : `/pabws/hrms/employee/profile/me`;
                const raw = await apiService.get<any>(
                    endpoint,
                    undefined,
                    {
                        includeAuthToken: true,
                        authHeaderType: "Bearer",
                        errorHandling: "return-null",
                        signal: ac.signal,
                    }
                );

                if (ac.signal.aborted) {
                    return;
                }
                if (gen !== profileLoadGenRef.current) {
                    return;
                }

                const record = parseHrmsEmployeeProfileGetResponse(raw) as
                    | Record<string, any>
                    | null;

                if (!record) {
                    dispatch(
                        showNotification({
                            message: "Could not load employee profile.",
                            type: "error",
                        })
                    );
                    dispatch(
                        setScreenFormData({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            data: {},
                        })
                    );
                    return;
                }

                const mapped = buildFormDataFromRecord(record, {
                    screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                });
                const rowId = record.id ?? record.employee_id ?? employeeId;
                dispatch(
                    setScreenFormData({
                        screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                        data: {
                            ...mapped,
                            id: rowId,
                            employee_id: String(rowId),
                        },
                    })
                );
            } catch {
                if (!ac.signal.aborted && gen === profileLoadGenRef.current) {
                    dispatch(
                        showNotification({
                            message: "Could not load employee profile.",
                            type: "error",
                        })
                    );
                    dispatch(
                        setScreenFormData({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            data: {},
                        })
                    );
                }
            } finally {
                if (gen === profileLoadGenRef.current) {
                    dispatch(
                        setScreenLoading({
                            screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                            isLoading: false,
                        })
                    );
                }
            }
        })();

        return () => {
            ac.abort();
            dispatch(
                setScreenLoading({
                    screenId: EMPLOYEE_PROFILE_SCREEN_ID,
                    isLoading: false,
                })
            );
        };
    }, [
        screenId,
        configLoading,
        config,
        user?.employee_id,
        refreshKey,
        dispatch,
    ]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-[50vh] flex-col gap-4">
                <div className="w-10 h-10" />
                <img
                    src={isDarkMode ? loaderDark : loaderLight}
                    alt="Loading"
                    className="w-00 h-80"
                />
                {configLoading && <p className="text-gray-500 text-sm">Loading configuration...</p>}
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                {error}
            </div>
        );
    }

    if (!config) {
        return (
            <div className="p-6 text-center text-danger-600 dark:text-danger-400">
                Screen configuration not found for ID: {screenId}
            </div>
        );
    }

    // Dev indicator for config source (Vite dev build, or localStorage dev_mode=true)
    const showDevIndicator = Boolean(source) && isDevIndicatorEnabled();

    return (
        <div className="relative">
            {showDevIndicator && (
                <div
                    className={`absolute top-0 right-0 z-50 px-2 py-1 text-xs font-bold rounded-bl-md shadow-sm opacity-90 border-l border-b flex items-center gap-1.5 ${
                        source === 'API'
                            ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/90 dark:text-blue-200 dark:border-blue-700'
                            : 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-amber-900/90 dark:text-amber-200 dark:border-amber-700'
                    }`}
                    title={`Configuration loaded from ${source} (Screen ID: ${screenId})`}
                >
                    <span className="pointer-events-none">Config: {source} | Screen: {screenId}</span>
                    <button
                        type="button"
                        className="inline-flex items-center justify-center p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors cursor-pointer text-current"
                        onClick={() => setConfigEditorOpen(true)}
                        title="Edit screen configuration"
                        aria-label="Edit screen configuration"
                    >
                        <EditIcon />
                    </button>
                </div>
            )}

            <ScreenConfigJsonEditor
                isOpen={configEditorOpen}
                onClose={() => setConfigEditorOpen(false)}
                config={config}
                screenId={screenId}
                source={source ?? undefined}
            />

            <div
                className={
                    isExpressionCalculating
                        ? "pointer-events-none select-none opacity-40"
                        : undefined
                }
                aria-busy={isExpressionCalculating || undefined}
            >
                <ComponentRenderer
                    config={popupSafeConfig}
                    formData={formData}
                    onFormDataChange={(name, value) =>
                        dispatch(updateScreenFormField({ screenId, name, value }))
                    }
                    onActionTrigger={onActionTrigger}
                    refreshKey={refreshKey}
                    renderMode={renderMode}
                    screenContextId={screenId}
                />
            </div>

            {isExpressionCalculating && (
                <div className="absolute inset-0 z-[60] flex justify-center items-center min-h-[50vh] flex-col gap-4 bg-white/70 dark:bg-gray-900/70">
                    <div className="w-10 h-10" />
                    <img
                        src={isDarkMode ? loaderDark : loaderLight}
                        alt="Loading"
                        className="w-00 h-80"
                    />
                    <p className="text-gray-500 text-sm">Calculating fields...</p>
                </div>
            )}
        </div>
    );
};
